import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EntityList from '../components/EntityList/EntityList';
import ChipBuilder from '../components/ChipBuilder';
import { ExportCSVButton } from '../components/ExportButtons';
import {
  listBookings,
  archiveBooking,
  updateBooking,
  listAllGolfCourses,
  isAdmin as checkAdmin,
  exportBookingsCSV,
} from '../api';
import { LIST_PAGE_SIZE } from '../constants';
import useEntityList from '../hooks/useEntityList';
import styles from './BookingsPage.module.css';
import HolePill from '../components/HolePill';
import { parseISOTime, formatHourMin, formatISODate } from '../utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(isoStr) {
  if (!isoStr) return '—';
  const { hour, min } = parseISOTime(isoStr);
  if (!isoStr.match(/T(\d{2}):(\d{2})/)) return '—';
  return formatHourMin(hour, min);
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export function buildColumns(styles) {
  return [
    {
      key: 'booking_number',
      label: 'Booking #',
      render: (value) => (
        <span className={styles.bookingNumber}>{value}</span>
      ),
    },
    {
      key: 'tee_time',
      label: 'Date',
      render: (tt) => formatISODate(tt?.start_time),
    },
    {
      key: 'tee_time_time',
      label: 'Time',
      render: (_, item) => formatTime(item.tee_time?.start_time),
    },
    {
      key: 'tee_time_course',
      label: 'Golf course',
      render: (_, item) => item.tee_time?.golf_course_name || '—',
    },
    {
      key: 'tee_time_hole',
      label: 'Hole',
      render: (_, item) => {
        const h = item.tee_time?.hole;
        if (h == null) return '—';
        return <HolePill hole={h} />;
      },
    },
    {
      key: 'booked_by_name',
      label: 'Booked by',
      render: (value) => value || '—',
    },
    {
      key: 'players',
      label: 'Players',
      render: (players) => (
        <span className={styles.playerCount}>
          {(players || []).length}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, item) => {
        const pills = [];
        if (item.is_cancelled)    pills.push(<span key="cancelled"   className={styles.badgeCancelled}>Cancelled</span>);
        if (item.is_archived)     pills.push(<span key="archived"    className={styles.badgeArchived}>Archived</span>);
        if (item.is_problematic)  pills.push(<span key="problematic" className={styles.badgeProblematic}>Problematic</span>);
        if (!item.is_validated)   pills.push(<span key="unvalidated" className={styles.badgeUnvalidated}>Unvalidated</span>);
        if (pills.length === 0) return null;
        return <div className={styles.statusPills}>{pills}</div>;
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Chip field definitions
// ---------------------------------------------------------------------------

const CHIP_FIELDS = [
  { key: 'date',           label: 'Date',        type: 'date'                                 },
  { key: 'booking_number', label: 'Booking #',   type: 'text',   placeholder: 'e.g. BK-1001' },
  { key: 'time',           label: 'Time',        type: 'text',   placeholder: 'e.g. 14:20'    },
  { key: 'booked_by',      label: 'Booked by',   type: 'text',   placeholder: 'Name…'         },
  { key: 'course',         label: 'Course',      type: 'select', options: null /* dynamic */   },
  { key: 'hole',           label: 'Hole',        type: 'select',
    options: [
      { value: '1',  label: 'Hole 1'  },
      { value: '10', label: 'Hole 10' },
    ],
  },
  { key: 'is_problematic', label: 'Problematic', type: 'select',
    options: [
      { value: 'true',  label: 'Yes' },
      { value: 'false', label: 'No'  },
    ],
  },
  { key: 'is_validated', label: 'Validated', type: 'select',
    options: [
      { value: 'true',  label: 'Yes' },
      { value: 'false', label: 'No'  },
    ],
  },
  { key: 'is_archived', label: 'Archived', type: 'select',
    options: [
      { value: 'false', label: 'Active'   },
      { value: 'true',  label: 'Archived' },
    ],
  },
  { key: 'is_cancelled', label: 'Cancelled', type: 'select',
    options: [
      { value: 'true',  label: 'Yes' },
      { value: 'false', label: 'No'  },
    ],
  },
];

// ---------------------------------------------------------------------------
// BookingsPage
// ---------------------------------------------------------------------------

const COLUMNS = buildColumns;

export default function BookingsPage({ user }) {
  const navigate = useNavigate();
  const isAdmin = checkAdmin(user);
  const [searchParams] = useSearchParams();

  const [exportError, setExportError] = useState('');

  const [chips, setChips] = useState(() => {
    const result = [];
    CHIP_FIELDS.forEach((field) => {
      const val = searchParams.get(field.key);
      if (!val) return;
      let display = val;
      if (field.options) {
        const opt = field.options.find((o) => o.value === val);
        if (opt) display = opt.label;
      }
      result.push({ field: field.key, label: field.label, value: val, display });
    });
    return result;
  });

  const filterDate          = useMemo(() => chips.find((c) => c.field === 'date')?.value || '', [chips]);
  const isArchived          = useMemo(() => chips.find((c) => c.field === 'is_archived')?.value === 'true', [chips]);
  const filterBookingNumber = useMemo(() => chips.find((c) => c.field === 'booking_number')?.value || '', [chips]);
  const filterTime          = useMemo(() => chips.find((c) => c.field === 'time')?.value || '', [chips]);
  const filterBookedBy      = useMemo(() => chips.find((c) => c.field === 'booked_by')?.value || '', [chips]);
  const filterCourse        = useMemo(() => chips.find((c) => c.field === 'course')?.value || '', [chips]);
  const filterHole          = useMemo(() => chips.find((c) => c.field === 'hole')?.value || '', [chips]);
  const filterIsProblematic = useMemo(() => chips.find((c) => c.field === 'is_problematic')?.value ?? null, [chips]);
  const filterIsValidated   = useMemo(() => chips.find((c) => c.field === 'is_validated')?.value ?? null, [chips]);
  const filterIsCancelled   = useMemo(() => chips.find((c) => c.field === 'is_cancelled')?.value ?? null, [chips]);

  // Courses for chip dropdown
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    listAllGolfCourses()
      .then((data) => setCourses(data.items || []))
      .catch(() => setCourses([]));
  }, []);

  const filters = useMemo(() => ({
    isArchived,
    date: filterDate,
    bookingNumber: filterBookingNumber,
    time: filterTime,
    bookedBy: filterBookedBy,
    golfCourseId: filterCourse,
    hole: filterHole,
    isProblematic: filterIsProblematic,
    isValidated: filterIsValidated,
    isCancelled: filterIsCancelled,
  }), [isArchived, filterDate, filterBookingNumber, filterTime, filterBookedBy, filterCourse, filterHole, filterIsProblematic, filterIsValidated, filterIsCancelled]);

  const exportParams = {
    isArchived,
    date: filterDate || undefined,
    bookingNumber: filterBookingNumber || undefined,
    time: filterTime || undefined,
    bookedBy: filterBookedBy || undefined,
    golfCourseId: filterCourse || undefined,
    hole: filterHole || undefined,
    isProblematic: filterIsProblematic != null ? filterIsProblematic === 'true' : undefined,
    isValidated:   filterIsValidated   != null ? filterIsValidated   === 'true' : undefined,
    isCancelled:   filterIsCancelled   != null ? filterIsCancelled   === 'true' : undefined,
  };

  const fetchFn = useCallback((cursor, { isArchived: archived, date, bookingNumber, time, bookedBy, golfCourseId, hole, isProblematic, isValidated, isCancelled }) =>
    listBookings({
      ...(date           ? { date }                                          : {}),
      isArchived: archived,
      limit: LIST_PAGE_SIZE,
      cursor,
      ...(bookingNumber  ? { bookingNumber }                                 : {}),
      ...(time           ? { time }                                          : {}),
      ...(bookedBy       ? { bookedBy }                                      : {}),
      ...(golfCourseId   ? { golfCourseId }                                  : {}),
      ...(hole           ? { hole }                                          : {}),
      ...(isProblematic != null ? { isProblematic: isProblematic === 'true' } : {}),
      ...(isValidated   != null ? { isValidated:   isValidated   === 'true' } : {}),
      ...(isCancelled   != null ? { isCancelled:   isCancelled   === 'true' } : {}),
    }),
  []);

  const {
    items,
    total,
    loading,
    fetchError,
    selectedIds,
    setSelectedIds,
    page,
    totalPages,
    handleNext,
    handlePrev,
    handleArchive,
    handleUnarchive,
  } = useEntityList({
    fetchFn,
    archiveFn: archiveBooking,
    unarchiveFn: (id) => updateBooking(id, { is_archived: false }),
    filters,
  });

  const columns = useMemo(() => COLUMNS(styles), []);

  const filterPanel = (
    <div>
      <ChipBuilder
        chips={chips}
        onChipsChange={setChips}
        fields={CHIP_FIELDS}
        dynamicOptions={{ course: courses.map((c) => ({ value: String(c.id), label: c.name })) }}
      />
      {exportError && <p className={styles.exportError}>{exportError}</p>}
    </div>
  );

  const addButton = (
    <ExportCSVButton
      exportFn={() => exportBookingsCSV(exportParams)}
      onError={setExportError}
      variant="list"
    />
  );

  return (
    <EntityList
      title="Bookings"
      columns={columns}
      items={items}
      total={total}
      loading={loading}
      errorMessage={fetchError}
      onRowClick={(item) => navigate(`/bookings/${item.id}`)}
      isAdmin={isAdmin}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      onArchive={handleArchive}
      filterPanel={filterPanel}
      page={page}
      totalPages={totalPages}
      onPrev={handlePrev}
      onNext={handleNext}
      addButton={addButton}
      showArchived={isArchived}
      onUnarchive={handleUnarchive}
    />
  );
}
